import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Settings, Users, MessageSquare, LogOut, Pencil, ShieldCheck, Download } from 'lucide-react'
import { useNav } from '@/shared/hooks/use-nav'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { PLAYER_POSITION_LABELS } from '@/core/models/user'
import { TopBar } from '@/shared/components/top-bar'
import { IconButton } from '@/shared/ui/icon-button'
import { Avatar } from '@/shared/ui/avatar'
import { Chip } from '@/shared/ui/chip'
import { StatPill } from '@/shared/ui/stat-pill'
import { ListItem } from '@/shared/ui/list-item'
import { Card } from '@/shared/ui/card'
import { Button } from '@/shared/ui/button'
import { authRepository } from '@/features/auth/api/auth-repository'
import { formatNivel } from '@/shared/levels'
import { formatLevelConfidence, formatVouching } from '@/shared/format'
import { getProfile, getProfileStats } from '@/shared/mock/data'
import { useMyMatches } from '@/features/partidos/use-matches'
import { usePublicProfile } from '@/features/jugadores/use-public-profile'
import { computePlayerStats } from './player-stats'
import { InstallSheet, useInstallAction } from '@/shared/components/install-sheet'
import { useInstall } from '@/shared/hooks/use-install'

// Player profile — header + stats + secondary nav (contactos/ajustes).
// Stats: bypass keeps the returning-player mock; real accounts derive them
// from THEIR matches (Partidos = recorded results, Racha = weekly streak —
// spec/usersmatch/05). No-show counters stay 0 until reliability persists.
export function PerfilPage() {
  const navigate = useNav()
  const mockFixture = getProfile()
  // ⚠️ THE IDENTITY BLOCK BELOW MUST BE THE PLAYER'S OWN.
  //
  // It rendered `getProfile()` — the design fixture — so every real player saw
  // "Marce Ruiz", Granada, nivel 3.4, Revés and a fabricated 85% reliability on
  // their OWN profile screen. Same defect as Editar perfil (fixed 2026-08-12),
  // on the screen a player looks at most, and it survived that fix because the
  // fix was applied to one file instead of to the class.
  //
  // `usePublicProfile(myUid)` was already fetched on this page for stats; it
  // carries name/city/position/level/reliability too. Nothing new to load.
  const myMatches = useMyMatches()
  const myUid = useAuthStore((s) => s.user?.uid ?? null)
  const myPub = usePublicProfile(AUTH_BYPASS ? null : myUid)
  /**
   * The identity shown on this screen. Under real auth it is the player's own
   * public projection; the fixture is used ONLY under the dev bypass, where
   * there is no account to read.
   */
  const me = AUTH_BYPASS
    ? {
        name: mockFixture.name,
        city: mockFixture.city,
        level: mockFixture.level as number | null,
        position: mockFixture.position as string | null,
        calibrated: mockFixture.levelCalibrated,
        reliability: mockFixture.levelReliability,
      }
    : {
        name: myPub.data?.name ?? '',
        city: myPub.data?.city ?? '',
        level: myPub.data?.skillLevel ?? null,
        position: myPub.data?.position ? PLAYER_POSITION_LABELS[myPub.data.position] : null,
        calibrated: myPub.data?.levelSource === 'computed',
        reliability: myPub.data?.levelReliability ?? null,
      }

  const { installed } = useInstall()
  const [installSheet, setInstallSheet] = useState(false)
  const onInstallClick = useInstallAction(setInstallSheet)
  // Both queries are async, and until 2026-08-06 neither had a branch — an
  // in-flight load painted "0 Partidos · 0 Ausencias", which is indistinguishable
  // from a real zero and quietly tells a new player their record is empty when we
  // simply have not read it yet. Bypass mode is synchronous mock data, so it
  // never waits.
  const statsPending = !AUTH_BYPASS && (myMatches.isPending || myPub.isPending)
  const statsFailed = !AUTH_BYPASS && (myMatches.isError || myPub.isError)
  const stats = AUTH_BYPASS
    ? getProfileStats()
    : {
        ...computePlayerStats(
          (myMatches.data ?? []).map((m) => ({
            dateISO: m.dateISO,
            hasResult: !!m.result,
            upcoming: m.status === 'active' || m.status === 'full',
          })),
          new Date(),
        ),
        // Ausencias come from MY public projection (spec/06) — the same
        // number every other player sees on my card.
        noShows: myPub.data?.noShows ?? 0,
        guestShows: myPub.data?.guestShows ?? 0,
        guestNoShows: myPub.data?.guestNoShows ?? 0,
      }
  // Null for the vast majority — players who have never brought anyone. Bound
  // here rather than called inline so the paragraph is not rendered empty.
  const vouchingLine = formatVouching(stats.guestShows, stats.guestNoShows, 'second')
  // The admin row only exists for the platform operator (custom claim); the
  // dev bypass shows it so the /admin surface is browsable in the stub.
  const isAdmin = useAuthStore((s) => s.isAdmin) || AUTH_BYPASS
  return (
    <>
      <TopBar
        title="Perfil"
        actions={
          <IconButton label="Ajustes" onClick={() => navigate('/ajustes')}>
            <Settings size={20} />
          </IconButton>
        }
      />

      <div className="px-4 pb-6 flex flex-col gap-4">
        {/* header */}
        <Section i={0}>
          <div className="flex flex-col items-center text-center gap-2 pt-2">
            <Avatar name={me.name || '?'} size={84} />
            <div>
              <p className="font-bold text-[length:var(--fs-xl)]">{me.name || 'Tu perfil'}</p>
              {me.city && <p className="text-[color:var(--fg-muted)]">{me.city}</p>}
            </div>
            <div className="flex gap-2">
              {me.level != null && <Chip>Nivel {formatNivel(me.level)}</Chip>}
              {me.position && <Chip>{me.position}</Chip>}
            </div>
            {/* Trust line (spec/05): where the nivel comes from, and how solid
                it is — through the SAME formatter as the player sheet. These two
                had drifted apart (here "fiabilidad 85%", there "Confirmado en
                pista"), which is exactly what shared/format.ts exists to stop:
                one phrasing per fact, app-wide. The raw percentage is gone on
                purpose — it is the ELO K-factor input (starts 10, +15 per
                competitive match, saturates in ~6), so a percentage promised
                precision the number never carried. */}
            <p className="flex items-center gap-1.5 text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
              <ShieldCheck size={13} className={me.calibrated ? 'text-[color:var(--ok)]' : ''} />
              {formatLevelConfidence(
                me.calibrated ? 'computed' : 'self',
                me.reliability,
              ) ?? 'Nivel autodeclarado — los partidos competitivos lo calibran'}
            </p>
            <Button variant="outline" size="pill" className="mt-1" onClick={() => navigate('/editar-perfil')}>
              <Pencil size={16} /> Editar perfil
            </Button>
          </div>
        </Section>

        {/* stats */}
        <Section i={1}>
          <div className="flex flex-col gap-2">
          {statsPending ? (
            <div className="flex gap-3" aria-busy>
              {['Partidos', 'Semanas seguidas', 'Ausencias'].map((l) => (
                <div key={l} className="flex-1 h-[72px] rounded-[var(--radius)] bg-[var(--panel-2)] motion-safe:animate-pulse" />
              ))}
            </div>
          ) : statsFailed ? (
            <p role="alert" className="text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)] py-3">
              No pudimos cargar tus estadísticas.{' '}
              <button
                type="button"
                className="text-[color:var(--accent)] underline min-h-[var(--tap)]"
                onClick={() => { void myMatches.refetch(); void myPub.refetch() }}
              >
                Reintentar
              </button>
            </p>
          ) : (
            <div className="flex gap-3">
              <StatPill value={stats.matches} label="Partidos" />
              <StatPill value={stats.streak} label="Semanas seguidas" />
              <StatPill value={stats.noShows} label="Ausencias" />
            </div>
          )}
          {/* Your vouching record, in the second person. Shown here and not only
              on the public sheet because this is the number that decides how
              many companions you may seat — and a limit you cannot see is a
              limit you experience as a bug. */}
          {!statsPending && !statsFailed && vouchingLine && (
            <p className="text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              {vouchingLine}
            </p>
          )}
          {stats.upcoming > 0 && (
            <p className="text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              {stats.upcoming === 1 ? '1 partido próximo' : `${stats.upcoming} partidos próximos`} en tu agenda
            </p>
          )}
          </div>
        </Section>

        {/* secondary nav */}
        <Section i={2}>
          <Card className="overflow-hidden divide-y divide-[var(--line)]">
            {!installed && (
              <ListItem
                leading={<Download size={20} className="text-[color:var(--fg-muted)]" />}
                title="Instalar la app"
                chevron
                onClick={() => void onInstallClick()}
              />
            )}
            <ListItem
              leading={<Users size={20} className="text-[color:var(--fg-muted)]" />}
              title="Contactos"
              chevron
              onClick={() => navigate('/contactos')}
            />
            <ListItem
              leading={<MessageSquare size={20} className="text-[color:var(--fg-muted)]" />}
              title="Enviar comentarios"
              chevron
              onClick={() => navigate('/ajustes?feedback=1')}
            />
            <ListItem
              leading={<Settings size={20} className="text-[color:var(--fg-muted)]" />}
              title="Ajustes"
              chevron
              onClick={() => navigate('/ajustes')}
            />
            {isAdmin && (
              <ListItem
                leading={<ShieldCheck size={20} className="text-[color:var(--fg-muted)]" />}
                title="Administración"
                chevron
                onClick={() => navigate('/admin')}
              />
            )}
          </Card>
        </Section>

        <Section i={3}>
          <button
            type="button"
            onClick={() => void authRepository.signOut()}
            className="w-full flex items-center justify-center gap-2 min-h-[var(--tap)] text-[color:var(--fg-muted)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform rounded-[var(--radius)]"
          >
            <LogOut size={18} /> Cerrar sesión
          </button>
        </Section>
      </div>
      <InstallSheet open={installSheet} onClose={() => setInstallSheet(false)} />
    </>
  )
}

function Section({ children, i }: { children: ReactNode; i: number }) {
  return (
    <div className="um-enter" style={{ '--i': i } as CSSProperties}>
      {children}
    </div>
  )
}
